import React, { useState, useEffect } from "react";
import { HelpCircle, UserX, Server, Wrench, Rocket } from "lucide-react";
import WelcomeSidebar from "@/components/welcome/WelcomeSidebar";
import WelcomeCard from "@/components/welcome/WelcomeCard";
import { lazy, Suspense } from "react";
const SatisfactionCard = lazy(() => import("@/components/welcome/SatisfactionCard"));
import ContactModal from "@/components/welcome/ContactModal";
import WhatElseCard from "@/components/welcome/WhatElseCard";
import RotatingWord from "@/components/welcome/RotatingWord";
import { useIsMobile } from "@/hooks/useIsMobile";

const cards = [
  {
    icon: HelpCircle,
    title: "I need help but don't even know what to ask!",
    description:
      "That's okay — most of our clients started right there. Tell us what's frustrating you and we'll translate it into a plan.",
    cta: "Start the conversation",
  },
  {
    icon: UserX,
    title: "I can't get ahold of my current web guy, what do I do?",
    description:
      "You're not stuck. We can help you regain access to your site, domain, and hosting — even if your old developer has vanished.",
    cta: "Get rescued",
  },
  {
    icon: Server,
    title: "Can you host my website?",
    description:
      "Absolutely. Fast, secure, fully managed hosting with a real human who answers the phone. Migration included.",
    cta: "Ask about hosting",
  },
  {
    icon: Wrench,
    title: "Help, my website is broken",
    description:
      "Errors, hacks, blank pages, expired plugins — we diagnose and fix broken websites quickly, then keep them healthy.",
    cta: "Request a fix",
  },
];

export default function Welcome() {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(isMobile);
  const [modalOpen, setModalOpen] = useState(false);
  const [topic, setTopic] = useState("");

  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  const openModal = (t) => {
    setTopic(t);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <WelcomeSidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        onContact={() => openModal("Let's talk")}
      />

      <main
        className={`transition-all duration-300 ${
          collapsed ? "ml-16" : "ml-16 lg:ml-60"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
          <div className="mb-8 sm:mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#F18D50] mb-2">
              Welcome to Host1Help1
            </p>
            {isMobile && (
              <img
                src="https://media.base44.com/images/public/69d14ed5d129627680ef5d0f/8209a6e84_host1help1-logo.svg"
                alt="Host1Help1"
                width="200"
                height="160"
                fetchpriority="high"
                className="block w-[180px] h-[144px] mb-4"
              />
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Let's take care of your <RotatingWord />
            </h1>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {cards.map((card, i) => (
              <WelcomeCard
                key={card.title}
                {...card}
                index={i}
                onClick={() => openModal(card.title)}
              />
            ))}
            <Suspense fallback={<div className="min-h-[280px]" />}>
              <SatisfactionCard index={4} />
            </Suspense>
            <WelcomeCard
              icon={Rocket}
              title="Ready when you are"
              description="One quick message is all it takes. No pressure, no jargon — just straight answers about your website and hosting."
              cta="Contact us now"
              index={5}
              onClick={() => openModal("Let's talk")}
            />
            <WhatElseCard index={6} />
          </div>
        </div>
      </main>

      <ContactModal open={modalOpen} onOpenChange={setModalOpen} topic={topic} />
    </div>
  );
}